"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import ShellFrame from "./ShellFrame";
import { getRailOnlyShellLabel } from "@/lib/route-layout";

/**
 * 아이콘 레일 전용 경량 셸 (JCG-2111).
 *
 * UI 2.0 전역 아이콘 레일(58px)만 적용하고 좌/우 사이드바는 두지 않는 와이드 본문 셸.
 * `/membership`(이용권 가격 비교)처럼 학습 도구(지도·목차·점수·풀이)도, 사이드바 카드/광고도
 * 필요 없지만 상단 NAV 대신 전역 레일 내비게이션은 유지하고 싶은 페이지를 위한 변형이다.
 *
 * - ShellFrame 을 columns="rail+main" 으로 렌더 → 좌측은 IconRail(항상)만, 우측 컬럼 없음.
 * - header 는 ContentHeader props 로 구성: route 기반 상시 제목(getRailOnlyShellLabel)을 staticTitle 로
 *   넘겨 핀 scroll-spy 없이 항상 표시하고, 좌측 사이드바 콘텐츠가 없으므로 토글(showSidebarToggle)은 숨긴다.
 *   검색창은 ContentHeader 의 기본 동작(우측 Search) 그대로.
 * - children(본문)은 ShellFrame 가운데로 그대로 전달 — **폭 제약을 강제하지 않는다**. 본문 max-w 캡은
 *   소비처(MembershipClient 의 max-w-[1400px] mx-auto)가 이미 가짐(IndexShell/TestCenterShell 처럼 셸이
 *   max-w 를 강제하면 와이드 가격 카드가 잘림).
 * - footer 는 ShellFrame 가운데 본문(main) 끝에, globals 는 ShellFrame 뒤(grid 바깥)에 둔다
 *   (IndexShell/TestCenterShell 의 배치와 동일).
 *
 * **폭 localStorage/리사이즈 핸들 없음** — rail-only 는 리사이즈 가능한 사이드바가 없어 폭 영속 키가
 * 불필요하다. ShellFrame 에 폭 prop(leftWidthCss/rightWidthCss/onResizeLeft/onResizeRight)을
 * 일절 전달하지 않는다(추가 금지) — columns="rail+main" 은 레일 고정 폭만 사용한다.
 */
interface RailShellProps {
  children: React.ReactNode;
  /** 서버 컴포넌트 (Footer) */
  footer: React.ReactNode;
  /** 서버 컴포넌트 (전역 핸들러) */
  globals: React.ReactNode;
  /** 경로로 제목을 판정할 수 없는 화면(404 등)의 상단바 제목. 없으면 route 기반 라벨을 쓴다(JCG-3679). */
  title?: string;
}

export default function RailShell({ children, footer, globals, title }: RailShellProps) {
  const pathname = usePathname() ?? "";
  const staticTitle = title ?? getRailOnlyShellLabel(pathname);

  return (
    <>
      <ShellFrame
        columns="rail+main"
        header={{
          categoryLabel: "",
          staticTitle,
          showSidebarToggle: false,
        }}
      >
        {children}
        {footer}
      </ShellFrame>
      {globals}
    </>
  );
}
